<script>
import { administrativeGender, ethnicityDetail, genderIdentity, languages, maritalStatus, pronouns, race, religiousAffiliation, sexualOrientation } from '@/common/api/terminology.api';
import { Address, Attachment, CodeableConcept, Coding, ContactPoint, PatientCommunication, traits } from '@/fhirworks';
import { getPhoneIcon, phoneUseValues, emailUseIcon, emailUseTextValues, addressUseIcon, addressUseValues } from '@/common/contactPoint';

import BnDatePicker from '@/components/BnDatePicker.vue';
import dataProvider from '@/components/DataProvider.js';

import { isPatient, sortByList } from '@/common/core.js';

import { getGoogleTimeZoneInfo } from '@/common/api/googleTimeZone.api.js';
import timezoneApi from '@/common/api/timezone.api.js';

import { expandValueSet } from '@/common/api/terminology.api.js';
import { isUser } from '@/common/core';
import { ENCOUNTER_STATUS_OPTIONS, ENCOUNTER_UI_CODE_TO_FHIR_STATUS, isReferrerEditable } from '@/common/episodeStatusConfig';
import { MANAGE_CONTACTS_EOC_PROGRAMS } from '@/common/permissionConstants';
import BnContactSearch from '@/components/BnContactSearch.vue';
import BnDialog from '@/components/BnDialog.vue';
import BnImageUploader from '@/components/BnImageUploader.vue';
import BnInfo from '@/components/BnInfo.vue';
import BnListBox from '@/components/BnListBox.vue';
import BnTooltip from '@/components/BnTooltip';
import BnUserSearch from '@/components/BnUserSearch';
import BnContactAddress from '@/components/contacts/BnContactAddress.vue';
import BnContactPointEdit from '@/components/contacts/BnContactPointEdit.vue';
import BnSmsConsentChip from '@/components/contacts/BnSmsConsentChip.vue';
import BnSmsConsentForm from '@/components/contacts/BnSmsConsentForm.vue';
import ContactPointSmsReminderConsent from '@/fhirworks/ContactPointSmsReminderConsent';
import { getConsentStatus as deriveConsentStatus, SMS_CONSENT_METHOD } from '@/fhirworks/constants/smsConsent';
import { useSmsConsent } from '@/composables/api/smsConsent/useSmsConsent';
import BnRelated from '@/components/contacts/BnRelated.vue';
import BnListIdentifiers from '@/components/lists/BnListIdentifiers.vue';
import BnListQualifications from '@/components/lists/BnListQualifications.vue';
import { usePatientAccess } from '@/composables/usePatientAccess';
import { useOutcomeToolsPatientSync } from '@/composables/useOutcomeToolsPatientSync';
import { useVuelidate } from '@vuelidate/core';
import { minLength, required, requiredIf } from '@vuelidate/validators';
import debounce from 'lodash/debounce';
import merge from 'lodash/merge';
import { useNotificationStore } from '@/stores/notification';

const raceCategoryLevel1Codes = ['1002-5', '2028-9', '2054-5', '2076-8', '2106-3', '2131-1'];
const raceCategoryCodeSystem = 'urn:oid:2.16.840.1.113883.6.238';
const V3_RACE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-Race';

// Every detailed race concept, each tagged with the top-level category it sits under.
const loadRaceDetailedConcepts = async () => {
    const perCategory = await Promise.all(
        raceCategoryLevel1Codes.map(async (category) => {
            const concepts = await expandValueSet({ systems: [{ system: V3_RACE_SYSTEM, filter: [{ property: 'concept', op: 'descendent-of', value: category }] }], all: true });
            return concepts.map((concept) => ({ code: concept.code, display: concept.display, category }));
        }),
    );
    return perCategory.flat();
};
const ethnicityCategoryCodeSystem = 'urn:oid:2.16.840.1.113883.6.238';
const raceCategoryData = [
    new Coding({
        code: '1002-5',
        system: raceCategoryCodeSystem,
        display: 'American Indian or Alaska Native',
    }),
    new Coding({
        code: '2028-9',
        system: raceCategoryCodeSystem,
        display: 'Asian',
    }),
    new Coding({
        code: '2054-5',
        system: raceCategoryCodeSystem,
        display: 'Black or African American',
    }),
    new Coding({
        code: '2076-8',
        system: raceCategoryCodeSystem,
        display: 'Native Hawaiian or Other Pacific Islander',
    }),
    new Coding({
        code: '2106-3',
        system: raceCategoryCodeSystem,
        display: 'White',
    }),
    new Coding({
        code: '2131-1',
        system: raceCategoryCodeSystem,
        display: 'Other Race',
    }),
];

const ethnicityCategoryHispanicLatinoCode = '2135-2';
const ethnicityCategoryNotHispanicLatinoCode = '2186-5';
const ethnicityCategoryData = [
    new Coding({
        code: ethnicityCategoryHispanicLatinoCode,
        system: ethnicityCategoryCodeSystem,
        display: 'Hispanic or Latino',
    }),
    new Coding({
        code: ethnicityCategoryNotHispanicLatinoCode,
        system: ethnicityCategoryCodeSystem,
        display: 'Not Hispanic or Latino',
    }),
];

const livingArrangementData = [
    new CodeableConcept({
        coding: [{ system: 'http://terminology.hl7.org/CodeSystem/v3-LivingArrangement', code: 'CS', display: 'Community shelter' }],
    }),
    new CodeableConcept({
        coding: [{ system: 'http://terminology.hl7.org/CodeSystem/v3-LivingArrangement', code: 'HL', display: 'Homeless' }],
    }),
    new CodeableConcept({
        coding: [{ system: 'http://terminology.hl7.org/CodeSystem/v3-LivingArrangement', code: 'M', display: 'Nomadic' }],
    }),
    new CodeableConcept({
        coding: [{ system: 'http://terminology.hl7.org/CodeSystem/v3-LivingArrangement', code: 'T', display: 'Transient' }],
    }),
];

const STATUS_DISPLAY = {
    PLANNED: 'Planned (approved)',
    ACTIVE: 'Active',
    WAITLIST: 'Waitlist (approved)',
    ELIGIBILITY_NOT_DETERMINED: 'Eligibility not determined',
    NOT_ELIGIBLE: 'Not eligible',
};

const episodeOfCareStatusData = [
    { code: 'eligibility-not-determined', display: STATUS_DISPLAY.ELIGIBILITY_NOT_DETERMINED },
    { code: 'planned', display: STATUS_DISPLAY.PLANNED },
    { code: 'waitlist', display: STATUS_DISPLAY.WAITLIST },
    { code: 'active', display: STATUS_DISPLAY.ACTIVE },
    { code: 'not-eligible', display: STATUS_DISPLAY.NOT_ELIGIBLE },
];

const encounterStatusData = ENCOUNTER_STATUS_OPTIONS;

export default {
    name: 'BnContactEditHuman',
    components: {
        BnTooltip,
        BnRelated,
        BnInfo,
        BnListIdentifiers,
        BnListQualifications,
        BnContactAddress,
        BnContactPointEdit,
        BnSmsConsentChip,
        BnSmsConsentForm,
        BnDialog,
        BnListBox,
        BnImageUploader,
        DataProvider: dataProvider,
        BnDatePicker,
        BnUserSearch,
        BnContactSearch,
    },
    props: {
        resource: Object,
        relationshipProps: Object,
        authStore: Object,
        isWidget: Boolean,
        autosave: Boolean,
        validations: Object,
        hideIdentifiers: Boolean,
        hideQualifications: Boolean, // Add this prop
        forceShowEoc: Boolean, // Force showing EOC section even for existing patients
    },
    emits: ['validate', 'showAccess', 'isPatientOrContact', 'eocPrepared', 'care-team-updated', 'data-changed', 'refresh-care-team', 'saved'],
    setup() {
        const { hasAccessToNewProgram } = usePatientAccess();
        const { recordConsentEvent } = useSmsConsent();
        const { loadActingPractitioner, canLinkPatient } = useOutcomeToolsPatientSync();
        return { v$: useVuelidate(), hasAccessToNewProgram, recordConsentEvent, loadActingPractitioner, canLinkPatient };
    },
    data() {
        const today = new Date().toISOString();
        return {
            humanData: null,
            dpAgencies: [],
            dpLocations: [],
            useValues: traits.contactPointTraits.contactPointUseValues,
            emailUseValues: traits.contactPointTraits.contactEmailUseValues,
            genderOptions: [],
            religiousAffiliationOptions: [],
            pronounOptions: [],
            sexualOrientationOptions: [],
            genderIdentityOptions: [],
            maritalStatusOptions: [],
            raceOptions: [],
            preferredLanguages: [],
            ethnicityDetailedOptions: [],
            sameAsHome: true,
            timezoneData: timezoneApi.loadPickerByRegion(),
            dpData: null,
            qualifications: [],
            raceCategoryOptions: raceCategoryData,
            raceDetailedOptions: [],
            raceDetailedSearch: '',
            raceParentLookup: null,
            raceDetailedConcepts: [],
            ethnicityDetailedSearch: '',
            debounceSearch: null,
            loadingRaceDetailedSearch: false,
            ethnicityCategoryOptions: ethnicityCategoryData,
            phoneUseValues,
            emailUseIcon,
            emailUseTextValues,
            addressUseIcon,
            addressUseValues,
            workingContactPoint: null,
            showContactPointDialog: false,
            pendingSmsConsents: {},
            smsConsentErrors: {},
            workingAddress: null,
            showAddressDialog: false,
            bithDateIsValid: true,
            livingArrangementOptions: livingArrangementData,
            statusCategoryOptions: episodeOfCareStatusData,
            episodeOfCareStatusData,
            encounterStatusData,
            // statusRationale: '',
            // hasExternalReferral: false,
            // referralDateTime: null,
            // referralUser: null,
            estimatedDateTime: today,
            // actualDateTime: null,
            eocStartDateTime: today.slice(0, 10),
            programStartDateTime: null,
            // selectedRationale: '',
            // customRationale: '',
            // referringUser: null,
            selectedProviders: [],
            primaryProvider: null,
            searchProvider: null,
            teamName: 'Care Team',
            careTeamData: [],
            addToOutcomeTools: false,
        };
    },
    validations() {
        return merge(this.validations, {
            humanData: {
                firstName: {
                    required,
                },
                ssn: {
                    minLength: minLength(11),
                },
                birthDate: {
                    customValidation: () => {
                        return this.isBirthDateValid;
                    },
                },
                temp: {
                    location: {
                        required: requiredIf(() => {
                            return this.showInitialEpisode;
                        }),
                    },
                    program: {
                        required: requiredIf(() => {
                            return this.showInitialEpisode;
                        }),
                    },
                },
                // Custom identifier fields must either both be filled or both be empty
                customIdentifierText: {
                    required: requiredIf(() => {
                        return this.customIdentifierValue && !this.customIdentifierText;
                    }),
                },
                customIdentifierValue: {
                    required: requiredIf(() => {
                        return this.customIdentifierText && !this.customIdentifierValue;
                    }),
                },
            },
        });
    },
    computed: {
        dpAgenciesQuery() {
            return [{ query: { partof: this.authStore.account.id, _sort: 'name' }, resourceType: 'Organization' }];
        },
        showImageUploader() {
            return !this.isWidget;
        },
        isBirthDateValid() {
            return this.bithDateIsValid;
        },
        isPatient() {
            return isPatient(this.resource);
        },
        canManageEpisodes() {
            return this.authStore.hasPermission(MANAGE_CONTACTS_EOC_PROGRAMS);
        },
        // A new patient always gets the section: a patient is never saved without an episode.
        // Adding an episode to an existing patient (forceShowEoc) needs the permission, because
        // the episode-of-care access policy denies the write without it.
        showInitialEpisode() {
            return this.isPatient && (!this.humanData?.id || (this.forceShowEoc && this.canManageEpisodes));
        },
        // Without the permission the server creates the episode with its defaults, so only
        // Location and Program are asked for.
        restrictedInitialEpisode() {
            return this.showInitialEpisode && !this.canManageEpisodes;
        },
        // Whether the user will be able to open the new patient's chart: the patient-read rule
        // keys on the program (Encounter) status, and an onlyCareTeam role needs the user on the
        // new care team. Null while there is no section or no location and program to judge.
        programAccess() {
            if (!this.showInitialEpisode || !this.eoc?.location?.id || !this.eoc?.program?.id) {
                return null;
            }
            return this.hasAccessToNewProgram({
                programId: this.eoc.program.id,
                locationId: this.eoc.location.id,
                status: ENCOUNTER_UI_CODE_TO_FHIR_STATUS[this.encounter?.status],
                onCareTeam: this.selectedProviders.some((provider) => provider.id === this.authStore.practitioner?.id),
            });
        },
        sortedRaceDetailedOptions() {
            return [...this.raceDetailedOptions].sort((a, b) => {
                return a.display > b.display ? 1 : -1;
            });
        },
        photo: {
            get: function () {
                return this.humanData?.photo?.[0]?.url;
            },
            set: function (newValue) {
                this.humanData.photo = [new Attachment({ url: newValue })];
            },
        },
        dpLocationsQuery() {
            return [
                {
                    query: '_include=organization&_include=location&practitioner=' + this.authStore?.practitioner?.id,
                    resourceType: 'PractitionerRole',
                },
                { query: 'active=true&isProgram=true', resourceType: 'HealthcareService' },
            ];
        },
        locations() {
            let locations = [];
            this.dpLocations
                .filter((item) => item.resourceType === 'Organization' && item.id === this.eoc?.agency?.id)
                .sort((a, b) => (a.name > b.name ? 1 : -1))
                ?.forEach((brand) => {
                    this.dpLocations
                        .filter((item) => item.resourceType === 'Location' && item.managingOrganization?.id === brand.id)
                        .sort((a, b) => (a.name > b.name ? 1 : -1))
                        ?.forEach((item) => {
                            locations.push({ id: item.id, display: item.name, managingOrganization: { id: brand.id, display: brand.name, resourceType: 'Organization' } });
                        });
                });
            return locations;
        },
        programs() {
            return (
                this.dpLocations
                    .filter((item) => item.resourceType === 'HealthcareService' && item.isProgram && item.location.some((loc) => loc.id === this.humanData.temp.location?.id))
                    .sort((a, b) => (a.name > b.name ? 1 : -1))
                    .flatMap((item) => {
                        return { id: item.id, display: item.name, resourceType: item.resourceType };
                    }) || []
            );
        },
        eoc() {
            return this.humanData?.temp;
        },
        referrerEditable() {
            return isReferrerEditable(this.encounter?.status);
        },
        encounter() {
            return this.humanData?.temp.encounter;
        },
        defaultProgram() {
            return this.dpLocations.find((item) => item.id === this.eoc?.location?.id)?.defaultProgram;
        },

        customIdentifierText: {
            get() {
                return this.humanData.customIdentifier ? this.humanData.customIdentifier.text : '';
            },
            set(value) {
                // Manually trigger the setter for customIdentifier
                this.humanData.customIdentifier = { text: value, value: this.customIdentifierValue };
            },
        },

        customIdentifierValue: {
            get() {
                return this.humanData.customIdentifier ? this.humanData.customIdentifier.value : '';
            },
            set(value) {
                // Manually trigger the setter for customIdentifier
                this.humanData.customIdentifier = { text: this.customIdentifierText, value };
            },
        },
        // Add this computed property for the care team query
        careTeamQuery() {
            if (!this.humanData?.id || !this.humanData?.temp?.encounter?.id) {
                return null;
            }
            const query = {
                resourceType: 'CareTeam',
                query: `encounter=${this.humanData.temp.encounter.id}&subject=${this.humanData.id}&_sort=-_lastUpdated&_count=1&_include=CareTeam:participant`,
            };
            return query;
        },
    },
    watch: {
        // Emits only; nothing in this component reads what the dialog does with it.
        programAccess: {
            handler(value) {
                this.$emit('showAccess', value);
            },
            immediate: true,
        },
        bithDateIsValid: {
            handler() {
                this.validateData();
            },
            immediate: true,
        },
        humanData: {
            async handler() {
                this.validateData();
            },
            immediate: true,
            deep: true,
        },
        raceDetailedSearch(value) {
            if (this.debounceSearch) {
                this.debounceSearch(value);
                return;
            }
            this.debounceSearch = debounce(this.searchRaceDetails, 500);
        },
        'eoc.status'(newStatus) {
            this.handleStatusChange(newStatus);
        },
        'humanData.temp.encounter.id': function (newEncounterId, oldEncounterId) {
            // If we now have an encounter ID, try to create a care team
            if (newEncounterId && !oldEncounterId) {
                // Refresh the data provider to check if a care team already exists
                if (this.$refs.careTeamDataProvider) {
                    this.$refs.careTeamDataProvider.refresh();
                } else {
                    // Try to create a care team directly
                    this.onCareTeamDataChanged();
                }
            }
        },
    },
    mounted() {
        if (this.relationshipProps?.scrollToControlRef) {
            this.$nextTick(() => {
                this.$refs[this.relationshipProps.scrollToControlRef].$el.scrollIntoView({ behavior: 'smooth' });
                // setTimeout used to pull the whole field into view
                setTimeout(() => this.$refs[this.relationshipProps.scrollToControlRef].focus(), 150);
            });
        }
        this.loadActingPractitioner();
    },
    async created() {
        // Load race detailed options so they are displayed
        this.raceDetailedOptions = this.resource.raceDetailed ? this.resource.raceDetailed.map((item) => item) : [];

        // languages,
        pronouns().then((options) => {
            if (options) {
                options = sortByList(
                    options.map((e) => e.resource),
                    ['LA29518-0', 'LA29519-8'],
                    'code',
                    'display',
                );
                this.pronounOptions = options.map((e) => {
                    return new Coding({
                        system: e.system,
                        code: e.code,
                        display: e.display,
                    });
                });
            }
        });

        sexualOrientation().then((options) => {
            if (options) {
                this.sexualOrientationOptions = options.map((e) => {
                    return new Coding({
                        system: e.resource.system,
                        code: e.resource.code,
                        display: e.resource.display,
                    });
                });
            }
        });

        genderIdentity().then((options) => {
            if (options) {
                options = sortByList(
                    options.map((e) => e.resource),
                    ['446151000124109', '446141000124107', 'ASKU'],
                    'code',
                    'display',
                );
                this.genderIdentityOptions = options.map((e) => {
                    return new CodeableConcept({
                        title: e.display,
                        coding: [{ system: e.system, code: e.code, display: e.display }],
                    });
                });
            }
        });

        maritalStatus().then((options) => {
            if (options) {
                options = options.map((e) => e.resource).sort((a, b) => (a.display > b.display ? 1 : -1));
                this.maritalStatusOptions = options.map((e) => {
                    return new CodeableConcept({
                        title: e.display,
                        coding: [{ system: e.system, code: e.code, display: e.display.toLowerCase().charAt(0).toUpperCase() + e.display.toLowerCase().slice(1) }],
                    });
                });
                // Dedupe options
                this.maritalStatusOptions = [...new Map(this.maritalStatusOptions.map((i) => [i.coding[0].code, i])).values()];
            }
        });

        languages().then((options) => {
            if (options) {
                options = sortByList(
                    options.map((e) => e.resource),
                    ['en', 'es', 'fr', 'de'],
                    'code',
                    'display',
                );
                this.preferredLanguages = options.map((e) => {
                    let languageCodeableConceptJson = {
                        coding: [{ system: 'urn:ietf:bcp:47', code: e.code, display: e.display }],
                        title: e.display,
                    };

                    if (this.resource.resourceType === 'Practitioner') {
                        return new CodeableConcept(languageCodeableConceptJson);
                    }
                    // Otherwise this is a Patient
                    return new PatientCommunication({
                        language: languageCodeableConceptJson,
                        preferred: true,
                    });
                });
            }
        });

        religiousAffiliation().then((options) => {
            if (options) {
                this.religiousAffiliationOptions = options.map((e) => {
                    return new CodeableConcept({
                        text: e.resource.display,
                        coding: [{ code: e.resource.code, display: e.resource.display }],
                    });
                });
            }
        });

        race().then((options) => {
            if (options) {
                this.raceOptions = options.map((e) => {
                    return new Coding({
                        system: e.resource.system,
                        code: e.resource.code,
                        display: e.resource.display,
                    });
                });
            }
        });

        ethnicityDetail().then((options) => {
            if (options) {
                this.ethnicityDetailedOptions = options.map((e) => {
                    return new Coding({
                        code: e.resource.code,
                        system: ethnicityCategoryCodeSystem,
                        display: e.resource.display,
                    });
                });
            }
        });

        administrativeGender().then((options) => {
            // debugger;
            if (options) {
                this.genderOptions = sortByList(
                    options.map((e) => e.resource),
                    ['male', 'female', 'other', 'unknown'],
                    'code',
                );
            }
        });
    },
    methods: {
        getPhoneIcon,
        prepareEpisodeOfCare() {
            if (!this.showInitialEpisode) return;
            let careTeamData = null;
            if (!this.humanData?.temp || Object.keys(this.humanData.temp).length === 0) return;

            // Handle status mapping explicitly
            let fhirStatus = 'planned';
            let eligibilityStatus = 'TBD';

            if (this.eoc.status === 'eligibility-not-determined') {
                fhirStatus = 'planned';
                eligibilityStatus = 'TBD';
            } else if (this.eoc.status === 'not-eligible') {
                fhirStatus = 'cancelled';
                eligibilityStatus = 'NotEligible';
            } else {
                fhirStatus = this.eoc.status;
                eligibilityStatus = null;
            }

            // Determine the proper status and eligibility codes for the new Encounter
            let episodeStatus = this.encounter.status;
            let episodeEligibility = null;
            if (episodeStatus === 'eligibility-not-determined') {
                episodeStatus = 'planned';
                episodeEligibility = 'TBD';
            } else if (episodeStatus === 'not-eligible') {
                episodeStatus = 'cancelled';
                episodeEligibility = 'NotEligible';
            }

            // Create care team if providers are selected
            if (this.selectedProviders && this.selectedProviders.length > 0) {
                // Schedule care team creation after the patient and episode are saved
                try {
                    // Prepare care team data
                    careTeamData = {
                        resourceType: 'CareTeam',
                        status: 'active',
                        name: 'Care Team',
                        encounter: {
                            id: this.humanData.temp.encounter.id,
                            resourceType: 'Encounter',
                        },
                        participant: this.selectedProviders.map((provider) => {
                            // Create a valid participant structure with member
                            const participant = {
                                member: {
                                    id: provider.id,
                                    resourceType: provider.resourceType || 'Practitioner',
                                    display: provider.display,
                                },
                            };

                            // Check if we need to add role information
                            const hasRoleText = provider.role && provider.role.trim() !== '';
                            const isPrimary = this.primaryProvider === provider.id;

                            // Only add role if needed
                            if (hasRoleText || isPrimary) {
                                participant.role = [
                                    {
                                        coding: [],
                                    },
                                ];

                                // Add text if it exists
                                if (hasRoleText) {
                                    participant.role[0].text = provider.role;
                                }

                                // Add primary coding if this is the primary provider
                                if (isPrimary) {
                                    participant.role[0].coding.push({
                                        system: 'http://terminology.hl7.org/CodeSystem/care-team-participant-role',
                                        code: 'primary',
                                        display: 'Primary',
                                    });
                                }
                            }

                            return participant;
                        }),
                    };
                    // Emit events to notify of care team creation
                    this.$emit('data-changed', {
                        careTeamId: savedCareTeam.id,
                    });
                    this.$emit('refresh-care-team');
                } catch (error) {
                    console.error('Error saving care team:', error);
                }
            }

            return {
                status: fhirStatus,
                eligibilityStatus,
                managingOrganization: {
                    id: this.eoc.agency.id,
                    resourceType: 'Organization',
                    display: this.eoc.agency.name,
                },
                location: {
                    id: this.eoc.location.id,
                    resourceType: 'Location',
                    display: this.eoc.location.display,
                },
                program: {
                    id: this.eoc.program.id,
                    resourceType: 'HealthcareService',
                    display: this.eoc.program.display,
                },
                period: this.eocStartDateTime
                    ? {
                          start: this.eocStartDateTime,
                      }
                    : { start: this.today.slice(0, 10) },
                estPeriod: this.eoc.eocEstStartDate
                    ? {
                          start: this.eoc.eocEstStartDate,
                      }
                    : { start: this.today.slice(0, 10) },
                statusReason: this.eoc.statusReason,
                admissionsOwner: this.eoc.admissionsOwner,
                encounterInformation: {
                    status: episodeStatus,
                    eligibility: episodeEligibility,
                    startDateTime: this.encounter.startDateTime,
                    estimatedStartDate: this.encounter.estimatedDateTime,
                    statusReason: this.encounter.statusReason,
                    referralSource: this.eoc.referralSource,
                },
                careTeam: careTeamData,
                addToOutcomeTools: this.addToOutcomeTools && this.canLinkPatient,
            };
        },

        setBirthDateisValid(isValid) {
            this.bithDateIsValid = isValid;
        },
        handleAddressLatLngChange: function (locationInfo) {
            if (!locationInfo) {
                this.humanData.timeZone = '';
                return;
            }
            getGoogleTimeZoneInfo(locationInfo).then(
                (res) => {
                    this.humanData.timeZone = res.data.timeZoneId;
                },
                () => {},
            );
        },
        practitionerLoaded() {
            // load existing qualification
            this.humanData?.qualification.forEach((item) => this.qualifications.push(item.code.coding[0]));
        },
        ethnicityCategoryChangeHandler(ethnicityCategory) {
            if (!(ethnicityCategory instanceof Coding)) {
                return;
            }

            if (ethnicityCategory.code === ethnicityCategoryNotHispanicLatinoCode) {
                this.humanData.ethnicityDetailed = undefined;
            }
        },
        ethnicityDetailedChangeHandler(detailItems) {
            this.ethnicityDetailedSearch = '';
            if (!Array.isArray(detailItems) || detailItems.length === 0) {
                return;
            }

            if (!this.humanData.ethnicityCategory || this.humanData.ethnicityCategory.code !== ethnicityCategoryHispanicLatinoCode) {
                this.humanData.ethnicityCategory = ethnicityCategoryData[0];
            }
        },
        async loadRaceParentLookup() {
            if (!this.raceParentLookup) {
                const concepts = await loadRaceDetailedConcepts();
                this.raceDetailedConcepts = concepts;
                this.raceParentLookup = new Map(concepts.map((concept) => [concept.code, concept.category]));
            }
            return this.raceParentLookup;
        },
        async searchRaceDetails(searchVal) {
            // clear results if no searchVal
            if (!searchVal) {
                this.raceDetailedOptions = this.resource.raceDetailed?.map((item) => item) || [];
                return;
            }

            this.loadingRaceDetailedSearch = true;
            try {
                await this.loadRaceParentLookup();
                const term = searchVal.toLowerCase();
                const results = this.raceDetailedConcepts.filter((concept) => concept.display.toLowerCase().includes(term));

                // reset list to currently selected items
                this.raceDetailedOptions = this.resource.raceDetailed?.map((item) => item) || [];

                // add new search results
                results.forEach((concept) => {
                    // Check if the race item already exist in the detailed options array
                    if (this.raceDetailedOptions.find((item) => item.code === concept.code)) {
                        return;
                    }
                    this.raceDetailedOptions.push(
                        new Coding({
                            code: concept.code,
                            system: raceCategoryCodeSystem,
                            display: concept.display,
                        }),
                    );
                });
            } catch (error) {
                console.error('Detailed race search failed', { search: searchVal, error });
                useNotificationStore().add({ message: 'Failed to search detailed races. Please try again.', color: 'error' });
            } finally {
                this.loadingRaceDetailedSearch = false;
            }
        },
        async raceDetailedChangeHandler(detailItems) {
            this.raceDetailedSearch = '';

            let raceParentLookup;
            try {
                raceParentLookup = await this.loadRaceParentLookup();
            } catch (error) {
                console.error('Detailed race category lookup failed', { codes: detailItems.map((item) => item.code), error });
                useNotificationStore().add({ message: 'Failed to set the race category. Please select it manually.', color: 'error' });
                return;
            }

            (detailItems || []).forEach((item) => {
                const parentTopCode = raceParentLookup.get(item.code);
                if (!parentTopCode) return;
                if (this.humanData.raceCategories.find((item) => item.code === parentTopCode)) {
                    return;
                }

                this.humanData.raceCategories = this.raceCategoryOptions.find((item) => {
                    return item.code === parentTopCode;
                });
            });
        },
        addPhone(phoneOption) {
            // If no phone parameter passed (mouse event found), default to mobile.
            if (phoneOption instanceof Event) phoneOption = 'mobile';

            let disablePreferredCheckBox = false;
            let contactJson = { system: 'phone', use: phoneOption };
            if (!this.humanData.allPhones.length) {
                contactJson.rank = 1;
                disablePreferredCheckBox = true;
            }
            this.workingContactPoint = new ContactPoint(contactJson);
            this.workingContactPoint.temp.mode = 'add';
            this.workingContactPoint.temp.disablePreferred = disablePreferredCheckBox;
            this.showContactPointDialog = true;
        },
        editPhone(phone) {
            this.workingContactPoint = phone;
            this.workingContactPoint.temp.mode = 'edit';
            this.workingContactPoint.temp.disablePreferred = false;
            if (this.workingContactPoint.rank === 1 && this.humanData.allPhones.length > 1) {
                this.workingContactPoint.temp.disablePreferred = true;
            }
            this.showContactPointDialog = true;
        },
        addEmail(emailOption) {
            // If no phone parameter passed (mouse event found), default to mobile.
            if (emailOption instanceof Event) emailOption = 'home';

            let disablePreferredCheckBox = false;
            let emailJson = { system: 'email', use: emailOption };
            if (!this.humanData.allEmails.length) {
                emailJson.rank = 1;
                disablePreferredCheckBox = true;
            }
            this.workingContactPoint = new ContactPoint(emailJson);
            this.workingContactPoint.temp.mode = 'add';
            this.workingContactPoint.temp.disablePreferred = disablePreferredCheckBox;
            this.showContactPointDialog = true;
        },
        editEmail(email) {
            this.workingContactPoint = email;
            this.workingContactPoint.temp.mode = 'edit';
            this.showContactPointDialog = true;
        },
        removeContactPoint(contactPoint) {
            let contactIndex = this.humanData.telecom.indexOf(contactPoint);
            if (contactIndex >= 0) {
                // If we are deleting a mobile phone that is the value for sendSms, clear
                // the resource sendSms value
                if (contactPoint.system === 'phone' && contactPoint.use === 'mobile' && this.humanData.sendSms === contactPoint.value) {
                    this.humanData.sendSms = undefined;
                }
                this.humanData.telecom.splice(contactIndex, 1);
                delete this.pendingSmsConsents[contactPoint.value];
                delete this.smsConsentErrors[contactPoint.value];
            }
        },
        // Exposes the consent-status helper to this Options API template.
        getConsentStatus(contactPoint) {
            return deriveConsentStatus(contactPoint);
        },
        // The consent endpoint already persisted the event and updated the patient's
        // telecom extension server-side (issue #4071). Mirror the new derived state onto
        // the working copy so the chip updates immediately and a later dialog Save preserves it.
        onConsentRecorded(contactPoint, consent) {
            if (consent) {
                contactPoint.smsReminderConsent = new ContactPointSmsReminderConsent(consent);
            }
        },
        onConsentStaged(contactPoint, staged) {
            if (staged) {
                this.pendingSmsConsents[contactPoint.value] = staged;
            } else {
                delete this.pendingSmsConsents[contactPoint.value];
            }
            delete this.smsConsentErrors[contactPoint.value];
            // Staging only. The dialog records on save; there is no autosave path here any more,
            // because the one autosave host (the portal demographics widget) no longer renders
            // verbal capture at all (#5369).
        },
        // The edit dialog passes the saved id — a just-created patient's id may not be on
        // humanData yet. Returns the failure count; failed entries stay staged.
        async commitPendingSmsConsents(patientId = this.humanData?.id) {
            if (!patientId) return 0;
            let failures = 0;
            for (const [telecomValue, staged] of Object.entries(this.pendingSmsConsents)) {
                const phone = this.humanData.telecom?.find((cp) => cp.value === telecomValue);
                if (!phone) {
                    // Number edited or removed after staging.
                    delete this.pendingSmsConsents[telecomValue];
                    continue;
                }
                try {
                    const result = await this.recordConsentEvent(patientId, {
                        telecomValue,
                        action: staged.action,
                        method: SMS_CONSENT_METHOD.VERBAL,
                        channel: staged.channel,
                        attestationVerbatim: staged.attestationVerbatim,
                    });
                    delete this.pendingSmsConsents[telecomValue];
                    this.onConsentRecorded(phone, result?.consent ?? null);
                } catch (error) {
                    this.smsConsentErrors[telecomValue] = error;
                    failures++;
                }
            }
            return failures;
        },
        addAddress(addressOption) {
            // If no phone parameter passed (mouse event found), default to mobile.
            if (addressOption instanceof Event) addressOption = 'home';

            let disablePreferredCheckBox = false;
            this.workingAddress = new Address({ use: addressOption });
            if (!this.humanData.address.length) {
                this.workingAddress.preferred = true;
                disablePreferredCheckBox = true;
            }
            this.workingAddress.temp.mode = 'add';
            this.workingAddress.temp.disablePreferred = disablePreferredCheckBox;
            this.showAddressDialog = true;
        },
        editAddress(address) {
            this.workingAddress = address;
            this.workingAddress.temp.mode = 'edit';
            this.workingAddress.temp.disablePreferred = false;
            if (this.workingAddress.preferred && this.humanData.address.length) {
                this.workingAddress.temp.disablePreferred = true;
            }
            this.showAddressDialog = true;
        },
        removeAddress(addressOption) {
            let addressIndex = this.humanData.address.indexOf(addressOption);
            if (addressIndex >= 0) {
                this.humanData.address.splice(addressIndex, 1);
            }
        },
        sortedAddress(addresses) {
            let addressSortOrder = {
                home: 1,
                mailing: 2,
                billing: 2,
                work: 3,
            };
            return addresses.map((address) => address).sort((a, b) => (a.preferred ? 0 : 1) - (b.preferred ? 0 : 1) || (addressSortOrder[a.use] > addressSortOrder[b.use] ? 1 : -1));
        },

        // Practitioner identifier/qualifications
        setIdentifier(identifier) {
            this.humanData.identifier = identifier;
        },

        resetPronounSelect() {
            this.humanData.pronouns = undefined;
        },

        // EOC
        initEOC() {
            this.setEOCComplaint('Unspecified');
            const defaultAgency = this.authStore.brand;
            const defaultLocation = this.locations.find((item) => item.id === this.authStore.brand.location.id);
            this.setEOCAgency(defaultAgency);
            this.setEOCLocation(defaultLocation);
            this.setEOCStatus('eligibility-not-determined');

            // Setup the program objects values
            const today = new Date().toISOString();
            this.humanData.temp.encounter = {
                status: 'eligibility-not-determined',
                startDateTime: today,
                estimatedDateTime: today,
            };
        },
        setEOCComplaint(value) {
            this.humanData.temp.complaint = value;
        },
        setEOCLocation(value) {
            this.humanData.temp.location = value;
            const defaultProgram = this.programs.find((item) => item.id === this.defaultProgram?.id) || this.programs[0];
            this.humanData.temp.program = defaultProgram;
        },
        setEOCAgency(value) {
            // Set the agency
            this.humanData.temp.agency = value;
            // Set the managingOrganization
            this.humanData.temp.managingOrganization = {
                id: value.id,
                display: value.name,
                resourceType: 'Organization',
            };
            // set the location
            const agencyLocation = value.location || this.locations.find((item) => item.managingOrganization.id === value.id);

            if (agencyLocation) {
                this.$nextTick(() => {
                    this.setEOCLocation(agencyLocation);
                });
            }
        },
        setEOCStatus(value) {
            this.humanData.temp.status = value;
        },
        setEOCEstStartDate(value) {
            this.humanData.temp.eocEstStartDate = value;
        },
        setEOCStatusReason(value) {
            this.humanData.temp.statusReason = value;
        },
        // Writes the same Encounter.referralSource reference the program dialog writes, so a
        // referrer captured here is visible and editable from the program dialog afterwards.
        setEOCReferrer(resource) {
            if (!resource?.id) {
                console.error('Invalid referrer selection:', resource);
                return;
            }
            this.humanData.temp.referralSource = {
                id: resource.id,
                display: resource.fullName || resource.name,
                resourceType: resource.resourceType,
            };
        },
        clearEOCReferrer() {
            this.humanData.temp.referralSource = null;
        },
        setStatusReason(value) {
            this.encounter.statusReason = value;
        },
        setPlacementStatus(value) {
            this.humanData.temp.placementStatus = value;
        },
        async saveRelationship(newContact) {
            return await this.$refs.contactRelationship.saveNewContactRelationship(newContact);
        },
        closeMenu(e) {
            if (this.$refs[e.toString()]) {
                setTimeout(() => {
                    this.$refs[e.toString()].isMenuActive = false;
                }, 10);
            }
        },
        setBirthDate(date) {
            this.humanData.birthDate = date;
        },
        async validateData() {
            let valid = await this.v$.humanData?.$validate();

            if (!valid) {
                this.v$.humanData.$touch();
            }
            this.$emit('isPatientOrContact', this.isPatient);
            this.$emit('validate', !valid);
        },
        isResourceUser(resource) {
            return isUser(resource);
        },

        handleStatusChange(newStatus, type = 'EOC') {
            const today = new Date().toISOString();
            if (type === 'EOC') {
                this.eoc.status = newStatus;

                this.eocStartDateTime = today.slice(0, 10);
                this.programStartDateTime = null;
                this.eoc.eocEstStartDate = today.slice(0, 10);
                this.eoc.estPeriod = null;
                this.eoc.holdPeriod = null;
                this.eoc.statusReason = null;
            } else {
                this.encounter.status = newStatus;

                this.encounter.startDateTime = today;
                this.encounter.estimatedDateTime = today;
                this.encounter.statusReason = null;
            }
        },
        setActiveProvider(user) {
            let provider = user.practitionerResource;

            if (!provider || !provider.id) {
                console.error('Invalid provider object:', provider);
                return;
            }

            const existingProvider = this.selectedProviders.find((p) => p.id === provider.id);

            if (!existingProvider) {
                const newProvider = {
                    id: provider.id,
                    display: provider.fullName,
                    resourceType: provider.resourceType,
                    role: '',
                    credentials: provider.credentials,
                };

                this.selectedProviders.push(newProvider);
            }

            this.searchProvider = null;
        },

        removeProvider(providerId) {
            this.selectedProviders = this.selectedProviders.filter((p) => p.id !== providerId);

            // If we're removing the primary provider, clear the primary provider
            if (this.primaryProvider === providerId) {
                this.primaryProvider = null;
            }
        },

        setPrimaryProvider(providerId) {
            // If clicking the current primary provider, unselect it
            if (this.primaryProvider === providerId) {
                this.primaryProvider = null;
            } else {
                this.primaryProvider = providerId;
            }
        },
        onCareTeamDataChanged(event) {
            this.careTeamData = event.data;
        },
        handlePrimaryCheckbox(providerId) {
            this.primaryProvider = providerId;
        },
        onSaved() {
            this.$emit('saved', this.humanData);
        },
    },
};
</script>

<template>
    <DataProvider v-model="humanData" :autosave="autosave" :resource="resource" :validator="v$.humanData" @saved="onSaved()">
        <template #default>
            <template v-if="$vuetify.display.xs">
                <v-row>
                    <v-col v-if="showImageUploader">
                        <BnImageUploader
                            ref="humanData"
                            v-model="photo"
                            :resource="humanData"
                            :options="{ size: 48 }"
                            :alt="humanData.resourceType + ' image'"
                            :contact-edit="true"
                            :avatar="true"
                            :vocab="authStore.clientVocab"
                            :is-public="humanData.resourceType !== 'Patient'"
                            placeholder
                            class="d-flex py-0 justify-center"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="d-flex py-0">
                        <v-text-field
                            v-model="humanData.firstName"
                            label="First name"
                            autofocus
                            v-bind="$bnVuelidateErrorExtractor(v$.humanData, 'firstName', true)"
                            data-cy="contactEditHumanFirstName"
                            :class="{ usprivacy: getUSPrivacy(resource) }"
                            @blur="v$.humanData.firstName.$touch()"
                        >
                        </v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="d-flex py-0">
                        <v-text-field
                            v-model="humanData.lastName"
                            label="Last name"
                            data-cy="contactEditHumanLastName"
                            :class="{ usprivacy: getUSPrivacy(resource) }"
                            v-bind="$bnVuelidateErrorExtractor(v$.humanData, 'lastName', true)"
                        ></v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="d-flex py-0">
                        <v-text-field v-model="humanData.middleName" label="Middle name" data-cy="contactEditHumanMiddleName" :class="{ usprivacy: getUSPrivacy(resource) }"></v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="d-flex py-0">
                        <v-text-field v-model="humanData.preferredName" label="Preferred name" data-cy="contactEditHumanPreferredName" :class="{ usprivacy: getUSPrivacy(resource) }">
                            <template #append>
                                <BnInfo type="dialog" size="lg"> The individual's preferred name. If specified the preferred name will display in quotes after their first name. </BnInfo>
                            </template>
                        </v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="d-flex py-0">
                        <v-text-field
                            v-model="humanData.givenPhoneticSpelling"
                            label="First name phonetic spelling"
                            data-cy="contactEditHumanGivenPhoneticSpelling"
                            :class="{ usprivacy: getUSPrivacy(resource) }"
                        >
                            <template #append>
                                <BnInfo type="dialog" size="lg">
                                    Spelling of the individuals name the way it sounds. Phonetic spelling will display in parentheses after their first name IF preferred name is not specified.
                                </BnInfo>
                            </template>
                        </v-text-field>
                    </v-col>
                </v-row>
                <v-row v-if="humanData.resourceType === 'Patient'">
                    <!-- Maiden or prior name-->
                    <v-col class="d-flex py-0">
                        <v-text-field v-model="humanData.maidenName" label="Maiden or prior name" :class="{ usprivacy: getUSPrivacy(resource) }"> </v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="py-0">
                        <BnDatePicker
                            id="contactEditHumanDateOfBirth"
                            v-model="humanData.birthDate"
                            dob
                            :input-date-format="authStore.inputDateFormat"
                            label="Date of birth"
                            :passed-class="getUSPrivacy(resource) ? 'usprivacy' : ''"
                            v-bind="$bnVuelidateErrorExtractor(v$.humanData, 'birthDate', true)"
                            @update:model-value="setBirthDate"
                        />
                    </v-col>
                </v-row>
                <!--MRN-->
                <v-row v-if="isPatient">
                    <v-col class="py-0">
                        <v-text-field v-model="humanData.mrn" label="MRN" :class="{ usprivacy: getUSPrivacy(resource) }" :disabled="true"></v-text-field>
                    </v-col>
                </v-row>
                <!--Job title-->
                <v-row v-if="humanData.resourceType === 'Practitioner'">
                    <v-col class="py-0">
                        <v-text-field v-model="humanData.jobTitle" label="Job title" />
                    </v-col>
                </v-row>
                <!--Credentials-->
                <v-row v-if="humanData.resourceType === 'Practitioner'">
                    <v-col class="py-0">
                        <v-text-field v-model="humanData.credentials" label="Credentials" />
                    </v-col>
                </v-row>
                <!--Custom Identifier Label-->
                <v-row>
                    <v-col class="py-0">
                        <v-text-field v-model="customIdentifierText" label="Custom ID number label" v-bind="$bnVuelidateErrorExtractor(v$.humanData, 'customIdentifierText', true)" style="flex: 1" />
                    </v-col>
                </v-row>
                <!--Custom Identifier Value-->
                <v-row>
                    <v-col class="py-0">
                        <v-text-field
                            v-model="customIdentifierValue"
                            label="Custom ID number"
                            v-bind="$bnVuelidateErrorExtractor(v$.humanData, 'customIdentifierValue', true)"
                            style="flex: 1"
                            class="usprivacy"
                        />
                    </v-col>
                </v-row>
            </template>
            <template v-else>
                <!-- Row 1: First, Middle, Last Names, Photo-->
                <v-row>
                    <v-col class="d-flex pb-0">
                        <v-text-field
                            v-model="humanData.firstName"
                            label="First name"
                            autofocus
                            v-bind="$bnVuelidateErrorExtractor(v$.humanData, 'firstName', true)"
                            data-cy="contactEditHumanFirstName"
                            :class="{ usprivacy: getUSPrivacy(resource) }"
                            @blur="v$.humanData.firstName.$touch()"
                        >
                        </v-text-field>
                    </v-col>
                    <v-col class="d-flex pb-0">
                        <v-text-field v-model="humanData.middleName" label="Middle name" data-cy="contactEditHumanMiddleName" :class="{ usprivacy: getUSPrivacy(resource) }"></v-text-field>
                    </v-col>
                    <v-col class="d-flex pb-0">
                        <v-text-field
                            v-model="humanData.lastName"
                            label="Last name"
                            data-cy="contactEditHumanLastName"
                            :class="{ usprivacy: getUSPrivacy(resource) }"
                            v-bind="$bnVuelidateErrorExtractor(v$.humanData, 'lastName', true)"
                        ></v-text-field>
                    </v-col>
                    <v-col v-if="showImageUploader" class="d-flex justify-center align-center py-0">
                        <BnImageUploader
                            ref="humanData"
                            v-model="photo"
                            :resource="humanData"
                            :options="{ size: 48 }"
                            :alt="humanData.resourceType + ' image'"
                            :contact-edit="true"
                            :avatar="true"
                            :vocab="authStore.clientVocab"
                            :is-public="humanData.resourceType !== 'Patient'"
                            placeholder
                            class="align-self-center py-0"
                        />
                    </v-col>
                </v-row>
                <!-- Row 2: Preferred name, first name phonetic, maiden/prior name-->
                <v-row>
                    <v-col cols="3" class="d-flex py-0">
                        <v-text-field v-model="humanData.preferredName" label="Preferred name" data-cy="contactEditHumanPreferredName" :class="{ usprivacy: getUSPrivacy(resource) }">
                            <template #append>
                                <BnInfo type="dialog" size="lg"> The individual's preferred name. If specified the preferred name will display in quotes after their first name. </BnInfo>
                            </template>
                        </v-text-field>
                    </v-col>
                    <v-col cols="3" class="d-flex py-0">
                        <v-text-field
                            v-model="humanData.givenPhoneticSpelling"
                            label="First name phonetic spelling"
                            data-cy="contactEditHumanGivenPhoneticSpelling"
                            :class="{ usprivacy: getUSPrivacy(resource) }"
                        >
                            <template #append>
                                <BnInfo type="dialog" size="lg">
                                    Spelling of the individuals name the way it sounds. Phonetic spelling will display in parentheses after their first name IF preferred name is not specified.
                                </BnInfo>
                            </template>
                        </v-text-field>
                    </v-col>
                    <!-- Patient: Maiden or prior name. Others: DOB-->
                    <v-col v-if="humanData.resourceType === 'Patient'" cols="3" class="d-flex py-0">
                        <v-text-field v-model="humanData.maidenName" label="Maiden or prior name" :class="{ usprivacy: getUSPrivacy(resource) }" data-cy="contactEditHumanMaidenName"> </v-text-field>
                    </v-col>
                    <v-col v-else cols="3" class="py-0">
                        <BnDatePicker
                            id="contactEditHumanDateOfBirth"
                            v-model="humanData.birthDate"
                            dob
                            :input-date-format="authStore.inputDateFormat"
                            label="Date of birth"
                            :passed-class="getUSPrivacy(resource) ? 'usprivacy' : ''"
                            v-bind="$bnVuelidateErrorExtractor(v$.humanData, 'birthDate', true)"
                            :class="{ usprivacy: getUSPrivacy(resource) }"
                            data-cy="contactEditHumanBirthDate"
                            @update:model-value="setBirthDate"
                            @is-date-valid="setBirthDateisValid"
                        />
                    </v-col>
                </v-row>
                <!-- Additional Row (For Users) : Credentials and Job Title -->
                <v-row v-if="humanData.resourceType === 'Practitioner'">
                    <v-col class="py-0" cols="3">
                        <v-text-field v-model="humanData.jobTitle" label="Job title" data-cy="editJobTitle" />
                    </v-col>
                    <v-col class="py-0" cols="3">
                        <v-text-field v-model="humanData.credentials" label="Credentials" data-cy="editCredentials" />
                    </v-col>
                </v-row>
                <!-- Row 3: Date of Birth (For Patients) + MRN + Custom ID -->
                <v-row>
                    <v-col v-if="humanData.resourceType === 'Patient'" cols="3" class="pt-0">
                        <BnDatePicker
                            id="contactEditHumanDateOfBirth"
                            v-model="humanData.birthDate"
                            dob
                            :input-date-format="authStore.inputDateFormat"
                            label="Date of birth"
                            :passed-class="getUSPrivacy(resource) ? 'usprivacy' : ''"
                            v-bind="$bnVuelidateErrorExtractor(v$.humanData, 'birthDate', true)"
                            :class="{ usprivacy: getUSPrivacy(resource) }"
                            data-cy="contactEditHumanBirthDate"
                            @update:model-value="setBirthDate"
                            @is-date-valid="setBirthDateisValid"
                        />
                    </v-col>
                    <v-col v-if="isPatient" cols="3" class="pt-0">
                        <v-text-field v-model="humanData.mrn" label="MRN" :class="{ usprivacy: getUSPrivacy(resource) }" :disabled="true"></v-text-field>
                    </v-col>
                    <v-col cols="3" class="pt-0">
                        <v-text-field v-model="customIdentifierText" label="Custom ID number label" v-bind="$bnVuelidateErrorExtractor(v$.humanData, 'customIdentifierText', true)" style="flex: 1" />
                    </v-col>
                    <v-col cols="3" class="pt-0">
                        <v-text-field
                            v-model="customIdentifierValue"
                            label="Custom ID number"
                            v-bind="$bnVuelidateErrorExtractor(v$.humanData, 'customIdentifierValue', true)"
                            style="flex: 1"
                            class="usprivacy"
                        />
                    </v-col>
                </v-row>
            </template>

            <BnRelated
                v-if="relationshipProps && relationshipProps.relationSubject && relationshipProps.relationSubject.id"
                ref="contactRelationship"
                :subject-id="relationshipProps.relationSubject.id"
                :subject-resource-type="relationshipProps.relationSubject.resourceType"
                :adding-new-contact="true"
                :new-contact-data="humanData"
                :is-emergency-contact="relationshipProps.isEmergencyContact"
                :is-legal-guardian="relationshipProps.isLegalGuardian"
                data-cy="contactEditHumanRelationship"
            ></BnRelated>

            <!-- New Patient Episode of Care details -->
            <template v-if="showInitialEpisode">
                <div class="bn-section-header">Initial Episode of Care</div>
                <br />

                <v-row v-if="!restrictedInitialEpisode">
                    <v-col cols="12" sm="6" class="d-flex py-0">
                        <!-- Agency -->
                        <DataProvider v-model="dpAgencies" collection refresh-on-change :query="dpAgenciesQuery" />
                        <v-autocomplete
                            v-model="eoc.agency"
                            label="Agency"
                            :items="dpAgencies"
                            item-title="display"
                            return-object
                            data-cy="patientLocation"
                            class="agency-select"
                            @update:model-value="setEOCAgency"
                        >
                            <template #item="{ item, props }">
                                <v-list-item v-bind="props" :title="null" class="agency-list-item">
                                    <div class="d-flex align-center w-100 gap-4">
                                        <font-awesome-icon :icon="['far', 'building-columns']" class="flex-shrink-0 mr-4" />
                                        <div class="text-truncate">
                                            {{ item.value.name }}
                                        </div>
                                    </div>
                                </v-list-item>
                            </template>
                            <template #selection="{ item }">
                                <div class="d-flex align-center w-100 gap-4">
                                    <font-awesome-icon :icon="['far', 'building-columns']" class="flex-shrink-0 mr-4" />
                                    <div class="text-truncate">
                                        {{ item.value.name }}
                                    </div>
                                </div>
                            </template>
                        </v-autocomplete>
                    </v-col>
                </v-row>

                <v-row v-if="!restrictedInitialEpisode">
                    <v-col cols="12" sm="6" class="py-0">
                        <v-autocomplete
                            v-model="eoc.status"
                            label="Episode status"
                            :items="episodeOfCareStatusData"
                            item-title="display"
                            item-value="code"
                            data-cy="episodeStatus"
                            @update:model-value="handleStatusChange"
                        />
                    </v-col>
                    <!-- Show start date for planned or active status -->
                    <v-col v-if="eoc.status === 'active'" cols="12" sm="6" class="py-0">
                        <BnDatePicker id="startDateTime" v-model="eocStartDateTime" :input-date-format="authStore.inputDateFormat" label="Start date" with-time data-cy="startDateTime" />
                    </v-col>

                    <!-- Show estimated start date for waitlist -->
                    <v-col v-if="eoc.status === 'planned'" cols="12" sm="6" class="py-0">
                        <BnDatePicker
                            id="eocEstStartDate"
                            v-model="eoc.eocEstStartDate"
                            :input-date-format="authStore.inputDateFormat"
                            label="Estimated start date"
                            @update:model-value="setEOCEstStartDate"
                        />
                    </v-col>

                    <!-- Show status reason for eligibility not determined and not eligible -->
                    <v-col v-if="eoc?.status === 'eligibility-not-determined' || eoc?.status === 'not-eligible' || eoc?.status === 'waitlist'" cols="12" sm="6" class="py-0">
                        <v-text-field v-model="eoc.statusReason" label="Status reason" @update:model-value="setEOCStatusReason" />
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12" sm="6" class="py-0">
                        <!-- Location -->
                        <DataProvider v-model="dpLocations" collection :query="dpLocationsQuery" @loaded="initEOC" />
                        <v-autocomplete
                            v-model="eoc.location"
                            label="Location"
                            :items="locations"
                            item-title="display"
                            return-object
                            data-cy="patientLocation"
                            v-bind="$bnVuelidateErrorExtractor(v$.humanData.temp, 'location', true)"
                            @blur="v$.humanData.temp.location.$touch()"
                            @update:model-value="setEOCLocation"
                        >
                            <template #item="{ item, props }">
                                <v-list-item v-bind="props" :title="null">
                                    <font-awesome-icon :icon="['far', 'map-marker-alt']" class="mr-4" />
                                    {{ item.title }}
                                </v-list-item>
                            </template>
                            <template #selection="{ item }">
                                <font-awesome-icon :icon="['far', 'map-marker-alt']" class="mr-4" />
                                {{ item.title }}
                            </template>
                        </v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="6" class="py-0">
                        <v-autocomplete
                            v-model="eoc.program"
                            label="Program"
                            :items="programs"
                            item-title="display"
                            return-object
                            data-cy="patientProgram"
                            v-bind="$bnVuelidateErrorExtractor(v$.humanData.temp, 'program', true)"
                            class="program-select"
                            @blur="v$.humanData.temp.program.$touch()"
                        >
                            <template #item="{ item, props }">
                                <v-list-item v-bind="props" :title="null" class="program-list-item">
                                    <div class="d-flex align-center w-100 gap-4">
                                        <font-awesome-icon :icon="['far', 'route']" class="flex-shrink-0 mr-4" />
                                        <div class="text-truncate">
                                            {{ item.title }}
                                        </div>
                                    </div>
                                </v-list-item>
                            </template>
                            <template #selection="{ item }">
                                <div class="d-flex align-center w-100 gap-4">
                                    <font-awesome-icon :icon="['far', 'route']" class="flex-shrink-0 mr-4" />
                                    <div class="text-truncate">
                                        {{ item.title }}
                                    </div>
                                </div>
                            </template>
                            <template v-if="programAccess === false" #prepend>
                                <BnTooltip type="info">
                                    <v-btn icon variant="text" size="small" color="warning">
                                        <font-awesome-icon :icon="['far', 'triangle-exclamation']" size="lg" />
                                    </v-btn>
                                    <template #message>
                                        <span
                                            >You do not have access to {{ authStore.clientVocab.toLowerCase() }}s in this program at this location and will not be able to access this
                                            {{ authStore.clientVocab.toLowerCase() }} once they are added.</span
                                        >
                                    </template>
                                </BnTooltip>
                            </template>
                        </v-autocomplete>
                    </v-col>
                </v-row>

                <v-row v-if="!restrictedInitialEpisode">
                    <v-col cols="12" sm="6" class="py-0">
                        <v-autocomplete
                            v-if="encounter?.status"
                            v-model="encounter.status"
                            label="Program status"
                            :items="encounterStatusData"
                            item-title="display"
                            item-value="code"
                            data-cy="programStatus"
                            @update:model-value="handleStatusChange(encounter.status, 'program')"
                        />
                    </v-col>

                    <!-- Show start date for planned or active status -->
                    <v-col v-if="encounter?.status === 'in-progress'" cols="12" sm="6" class="py-0">
                        <BnDatePicker
                            id="encounterProgramStartDateTime"
                            v-model="encounter.startDateTime"
                            :input-date-format="authStore.inputDateFormat"
                            label="Start date"
                            with-time
                            data-cy="encounterStartDateTime"
                        />
                    </v-col>

                    <!-- Show estimated start date for waitlist -->
                    <v-col v-if="encounter?.status === 'planned' || encounter?.status === 'waitlist'" cols="12" sm="6" class="py-0">
                        <BnDatePicker id="encountErestimatedDateTime" v-model="encounter.estimatedDateTime" :input-date-format="authStore.inputDateFormat" label="Estimated start date" />
                    </v-col>

                    <!-- Show status reason for eligibility not determined and not eligible -->
                    <v-col v-if="encounter?.status === 'eligibility-not-determined' || encounter?.status === 'not-eligible' || encounter?.status === 'triaged'" cols="12" sm="6" class="py-0">
                        <v-text-field v-model="encounter.statusReason" label="Status reason" @update:model-value="setStatusReason" />
                    </v-col>
                </v-row>

                <v-row v-if="!restrictedInitialEpisode">
                    <!-- Referrer: writes Encounter.referralSource, the same field the program dialog edits -->
                    <v-col cols="12" sm="6" class="py-0">
                        <v-select
                            v-if="eoc?.referralSource?.id"
                            :model-value="eoc.referralSource"
                            label="Referrer"
                            :items="[eoc.referralSource]"
                            item-title="display"
                            item-value="id"
                            data-cy="eocReferrer"
                            :disabled="!referrerEditable"
                            clearable
                            @click:clear="clearEOCReferrer"
                        />
                        <BnContactSearch
                            v-else
                            label="Referrer"
                            placeholder="Search contacts"
                            no-add
                            :hide-details="false"
                            :disabled="!referrerEditable"
                            default-query="brandUsers"
                            :search-resources="['practitioner', 'patient', 'organization']"
                            data-cy="eocReferrerSearch"
                            @item-clicked="setEOCReferrer"
                        />
                    </v-col>

                    <!-- Admissions owner: not provider-only, since admissions coordinators are not billable providers -->
                    <v-col cols="12" sm="6" class="py-0">
                        <BnUserSearch v-model="eoc.admissionsOwner" label="Admissions owner" placeholder="Search staff" data-cy="eocAdmissionsOwner" />
                    </v-col>
                </v-row>

                <v-row v-if="canLinkPatient && !restrictedInitialEpisode">
                    <v-col cols="12" sm="6" class="py-0">
                        <v-checkbox v-model="addToOutcomeTools" label="Add to OutcomeTools&#8480;" density="compact" hide-details data-cy="addToOutcomeTools" />
                    </v-col>
                </v-row>

                <!-- After the Initial Episode of Care fields -->
                <div v-if="isPatient && !restrictedInitialEpisode" class="mt-6">
                    <div class="bn-section-header">CARE TEAM</div>

                    <!-- Selected Providers Section - Moved above search -->
                    <div v-if="selectedProviders.length" class="mb-4">
                        <v-list>
                            <v-list-item v-for="provider in selectedProviders" :key="provider.id" class="mb-3 pa-2">
                                <div class="d-flex flex-column">
                                    <v-list-item-title class="text-truncate" style="max-width: 200px">
                                        {{ provider.display || provider.fullName || provider.name?.[0]?.text || 'Unknown Provider'
                                        }}<span v-if="provider.credentials">, {{ provider.credentials }}</span>
                                    </v-list-item-title>

                                    <v-text-field
                                        v-model="provider.role"
                                        placeholder="Provider role (optional)"
                                        variant="underlined"
                                        density="compact"
                                        hide-details
                                        class="mt-1"
                                        style="max-width: 180px"
                                        hint="Custom role description"
                                        persistent-hint
                                    ></v-text-field>
                                </div>

                                <template #append>
                                    <div class="d-flex align-center">
                                        <v-checkbox
                                            v-model="primaryProvider"
                                            :value="provider.id"
                                            label="Primary provider"
                                            hide-details
                                            density="compact"
                                            class="primary-provider-checkbox"
                                            @click="handlePrimaryCheckbox(provider.id)"
                                        ></v-checkbox>
                                        <v-btn icon size="x-large" variant="text" color="error" @click="removeProvider(provider.id)">
                                            <font-awesome-icon :icon="['far', 'minus-circle']" />
                                        </v-btn>
                                    </div>
                                </template>
                            </v-list-item>
                        </v-list>
                    </div>

                    <!-- Search Section - Moved below selected providers -->
                    <div>
                        <BnUserSearch
                            v-model="searchProvider"
                            label="Search practitioners"
                            placeholder="Search practitioners"
                            autofocus
                            close-on-content-click="false"
                            persistent
                            :provider-only="true"
                            :auto-clear="true"
                            :menu-props="{ closeOnContentClick: true }"
                            @item-click="setActiveProvider"
                        />
                    </div>
                </div>
            </template>

            <BnListBox title="Phone" add data-cy="contactEditHumanPhoneList" @add="addPhone">
                <template #default>
                    <v-card v-if="!humanData.allPhones.length" flat class="d-flex text-caption pa-2" :border="false" @click="addPhone">
                        <div class="ml-auto">
                            Add phone
                            <font-awesome-icon :icon="['far', 'level-up']" class="ml-1 mr-3 mb-1" />
                        </div>
                    </v-card>
                    <template v-for="(phone, index) in humanData.allPhones.sort((a, b) => ((a.rank || 0) > (b.rank || 0) ? -1 : 1))" v-else :key="index">
                        <v-divider v-if="index"></v-divider>
                        <v-list-item density="compact" class="pr-0" data-cy="contactEditHumanPhoneItem" @click="editPhone(phone)">
                            <template #prepend>
                                <template v-if="phone.rank === 1">
                                    <font-awesome-icon :icon="['fas', 'star']" class="text-primary mr-2 align-self-center" />
                                </template>
                                <font-awesome-icon :icon="['far', getPhoneIcon(phone)]" class="mr-2 align-self-center" />
                            </template>
                            <v-list-item-subtitle>
                                <div class="bn-label">
                                    {{ phone.system === 'fax' ? 'Fax' : phoneUseValues[phone.use] }}
                                </div>
                            </v-list-item-subtitle>
                            <v-list-item-title :class="{ usprivacy: getUSPrivacy(resource) }">{{ phone.value }}</v-list-item-title>
                            <div>
                                <v-chip v-if="phone.doNotContact">
                                    <font-awesome-icon :icon="['far', 'ban']" />
                                    &nbsp; Do Not Contact
                                </v-chip>
                                <!-- Patients use the 4-state SMS reminder consent chip (issue #4071);
                                     Practitioners keep the legacy "Accepts SMS" chip. -->
                                <!-- Wrapped so the chip's popover click doesn't bubble to the list item's edit handler. -->
                                <span v-if="isPatient && phone.use === 'mobile'" @click.stop>
                                    <BnSmsConsentChip :status="getConsentStatus(phone)" :date="phone.smsReminderConsent?.lastUpdatedAt" :method="phone.smsReminderConsent?.method" />
                                </span>
                                <v-chip v-else-if="!isPatient && humanData.sendSms === phone.value">
                                    <font-awesome-icon :icon="['far', 'comment-alt']" />
                                    &nbsp; Accepts SMS
                                </v-chip>
                            </div>
                            <!-- Verbal capture is staff-only: the script, channel and "read verbatim"
                                 attestation describe something a staff member did, so recording a patient's
                                 own self-service answer that way would write a false attestation into an
                                 append-only audit trail (#5369). isWidget is the embedded autosave host
                                 (the portal demographics widget); patients consent through the SMS consent
                                 widget instead, which records method digital-form.
                                 Wrapped with @click.stop so interacting with the consent form
                                 (checkbox, channel, buttons) doesn't trigger the list item's edit handler. -->
                            <div v-if="isPatient && !isWidget && phone.use === 'mobile'" class="mt-2" @click.stop>
                                <BnSmsConsentForm
                                    :contact-point="phone"
                                    :staged="pendingSmsConsents[phone.value] || null"
                                    :error="smsConsentErrors[phone.value] || null"
                                    @update:staged="onConsentStaged(phone, $event)"
                                />
                            </div>
                            <template #append>
                                <BnTooltip v-if="phone.rank === 1 && humanData.allPhones.length > 1" bottom type="info">
                                    <v-btn class="d-flex align-center mr-2" icon variant="text" size="small" color="red" disabled>
                                        <font-awesome-icon :icon="['far', 'minus-circle']" size="lg" />
                                    </v-btn>
                                    <template #message>
                                        <span>A preferred phone number must be designated.</span>
                                    </template>
                                </BnTooltip>
                                <v-btn v-else class="d-flex align-center mr-2" icon variant="text" size="small" color="red" @click.stop="removeContactPoint(phone)">
                                    <font-awesome-icon :icon="['far', 'minus-circle']" size="lg" />
                                </v-btn>
                            </template>
                        </v-list-item>
                    </template>
                </template>
            </BnListBox>

            <BnListBox title="Email" add data-cy="contactEditHumanEmailList" @add="addEmail">
                <template #default>
                    <v-card v-if="!humanData.allEmails.length" flat class="d-flex text-caption pa-2" :border="false" @click="addEmail">
                        <div class="ml-auto">
                            Add email
                            <font-awesome-icon :icon="['far', 'level-up']" class="mx-1 mb-1" />
                        </div>
                    </v-card>
                    <template v-for="(email, index) in humanData.allEmails.sort((a, b) => ((a.rank || 0) > (b.rank || 0) ? -1 : 1))" v-else :key="index">
                        <v-divider v-if="index"></v-divider>
                        <v-list-item density="compact" class="pr-0" data-cy="contactEditHumanEmailItem" @click="editEmail(email)">
                            <template #prepend>
                                <div v-if="email.rank === 1" class="mr-2 align-self-center">
                                    <font-awesome-icon :icon="['fas', 'star']" class="text-primary" />
                                </div>
                                <div v-else class="mr-2 align-self-center">
                                    <font-awesome-icon v-if="email.use === 'work'" :icon="['far', emailUseIcon[email.use]]" />
                                    <font-awesome-icon v-else :icon="['far', 'at']" />
                                </div>
                            </template>
                            <v-list-item-subtitle>
                                <div class="bn-label">
                                    {{ emailUseTextValues[email.use] }}
                                </div>
                            </v-list-item-subtitle>
                            <v-list-item-title :class="{ usprivacy: getUSPrivacy(resource) }">{{ email.value }}</v-list-item-title>
                            <div>
                                <v-chip v-if="email.doNotContact" text>
                                    <font-awesome-icon :icon="['far', 'ban']" />
                                    &nbsp; Do Not Contact
                                </v-chip>
                            </div>
                            <template #append>
                                <v-btn class="d-flex mr-2" icon variant="text" size="small" color="red" @click.stop="removeContactPoint(email)">
                                    <font-awesome-icon :icon="['far', 'minus-circle']" size="lg" />
                                </v-btn>
                            </template>
                        </v-list-item>
                    </template>
                </template>
            </BnListBox>

            <BnListBox title="Address" add="Add Address" data-cy="contactEditHumanAddressList" :required="v$.humanData.address?.required && !humanData.address.length" @add="addAddress">
                <template #default>
                    <v-card v-if="!humanData.address.length" flat class="d-flex text-caption pa-2" :border="false" @click="addAddress">
                        <div class="ml-auto">
                            Add address
                            <font-awesome-icon :icon="['far', 'level-up']" class="ml-1 mr-3 mb-1" />
                        </div>
                    </v-card>
                    <template v-for="(addressItem, index) in sortedAddress(humanData.address)" v-else :key="index">
                        <v-divider v-if="index"></v-divider>
                        <v-list-item density="compact" class="pr-0" data-cy="contactEditHumanAddressItem" @click="editAddress(addressItem)">
                            <template #prepend>
                                <div v-if="addressItem.preferred" class="mr-2 align-self-center">
                                    <font-awesome-icon :icon="['fas', 'star']" class="text-primary" />
                                </div>
                                <div v-else class="mr-2 align-self-center">
                                    <font-awesome-icon v-if="addressItem.use" :icon="['far', addressUseIcon[addressItem.use]]" />
                                </div>
                            </template>
                            <v-list-item-subtitle class="bn-label">{{ addressUseValues[addressItem.use] }}</v-list-item-subtitle>
                            <v-list-item-title :class="{ usprivacy: getUSPrivacy(resource) }"
                                >{{ addressItem.getAddressLine(0) }}<br />{{ addressItem.city + ' ' + addressItem.state + ' ' + addressItem.postalCode }} <br />
                                {{ addressItem.district ? addressItem.district : '' }}</v-list-item-title
                            >
                            <template #append>
                                <BnTooltip v-if="addressItem.preferred && humanData.address.length > 1" bottom type="info">
                                    <v-btn class="d-flex align-center mr-2" icon variant="text" size="small" color="red" disabled>
                                        <font-awesome-icon :icon="['far', 'minus-circle']" size="lg" />
                                    </v-btn>
                                    <template #message>
                                        <span>A preferred address must be designated.</span>
                                    </template>
                                </BnTooltip>
                                <v-btn v-else class="d-flex align-center mr-2" icon variant="text" size="small" color="red" @click.stop="removeAddress(addressItem)">
                                    <font-awesome-icon :icon="['far', 'minus-circle']" size="lg" />
                                </v-btn>
                            </template>
                        </v-list-item>
                    </template>
                </template>
            </BnListBox>

            <v-autocomplete
                v-if="isPatient"
                v-model="humanData.livingArrangement"
                label="Lacking permanent address"
                :items="livingArrangementOptions"
                return-object
                item-title="coding[0].display"
                clearable
                placeholder="No"
                persistent-placeholder
                data-cy="contactEditHumanLivingArrangement"
                @click:clear="humanData.livingArrangement = undefined"
            >
                <template #no-data>
                    <v-list-item>
                        <v-list-item-title> No option found </v-list-item-title>
                    </v-list-item>
                </template>
            </v-autocomplete>
            <v-autocomplete
                v-model="humanData.timeZone"
                label="Timezone"
                :items="timezoneData"
                item-title="timezone"
                item-value="timezone"
                clearable
                placeholder="Select timezone"
                data-cy="contactEditHumanTimeZone"
            >
                <template #no-data>
                    <v-list-item>
                        <v-list-item-title> Timezone not found </v-list-item-title>
                    </v-list-item>
                </template>
                <template #item="{ item, props }">
                    <v-list-subheader v-if="item.raw.header">
                        {{ item.raw.header }}
                    </v-list-subheader>
                    <v-divider v-else-if="item.raw.divider" />
                    <v-list-item v-else v-bind="props" />
                </template>
            </v-autocomplete>

            <template v-if="humanData.resourceType === 'Practitioner'">
                <!-- User Qualifications -->
                <BnListQualifications v-if="!hideQualifications" v-model="humanData.qualification" data-cy="contactEditHumanQualifications" @update:model-value="humanData.qualification = $event" />
                <!-- User Identifiers -->
                <BnListIdentifiers v-if="!hideIdentifiers" v-model="humanData.identifier" title="Practitioner identifiers" type="Practitioner" data-cy="contactEditHumanIdentifier" />
            </template>

            <template v-if="!isResourceUser(humanData)">
                <div class="bn-section-header">Gender</div>
                <div class="d-flex">
                    <v-select v-model="humanData.gender" label="Sex" :items="genderOptions" item-title="display" item-value="code" data-cy="contactEditHumanGender">
                        <template #append>
                            <BnInfo type="dialog" size="lg"> Current biological and physiological sex. </BnInfo>
                        </template>
                        <template #no-data>
                            <v-list-item>
                                <v-list-item-title> Sex not found </v-list-item-title>
                            </v-list-item>
                        </template>
                    </v-select>
                </div>
            </template>
            <div class="d-flex">
                <v-select
                    v-if="humanData.resourceType === 'Patient'"
                    v-model="humanData.genderIdentity"
                    label="Gender identity"
                    :items="genderIdentityOptions"
                    item-title="coding[0].display"
                    return-object
                    data-cy="contactEditHumanGenderIdentity"
                >
                    <template #append>
                        <BnInfo size="lg" type="dialog"> The personal sense of being a man, woman, or other gender, regardless of the sex that person was assigned at birth. </BnInfo>
                    </template>
                </v-select>
            </div>
            <v-select
                v-if="!isResourceUser(humanData)"
                v-model="humanData.pronouns"
                label="Pronouns"
                :items="pronounOptions"
                item-title="display"
                return-object
                clearable
                data-cy="contactEditHumanPronouns"
                @click:clear="resetPronounSelect"
            ></v-select>
            <!-- <v-select label="Sexual Orientation" v-model="humanData.sexualOrientation" :items="sexualOrientationOptions" item-text="display" return-object clearable></v-select>-->

            <template v-if="humanData.resourceType === 'Patient'">
                <div class="bn-section-header">Race & Ethnicity</div>
                <v-select
                    ref="raceSelect"
                    v-model="humanData.raceCategories"
                    label="Race"
                    :items="raceCategoryOptions"
                    item-title="display"
                    return-object
                    multiple
                    small-chips
                    deletable-chips
                    clearable
                    data-cy="raceCategories"
                >
                    <template #prepend-item>
                        <div class="pa-2 text-caption d-flex justify-space-between">
                            Press the ESC key to close this list
                            <button class="mr-2" @click="closeMenu('raceSelect')">
                                <font-awesome-icon size="lg" :icon="['far', 'times']" class="mr-2" />
                            </button>
                        </div>
                    </template>
                </v-select>
                <v-autocomplete
                    v-if="!isWidget"
                    ref="raceDetailedSelect"
                    v-model="humanData.raceDetailed"
                    v-model:search="raceDetailedSearch"
                    label="Race detailed"
                    :loading="loadingRaceDetailedSearch"
                    :items="sortedRaceDetailedOptions"
                    item-title="display"
                    searchable
                    return-object
                    multiple
                    small-chips
                    deletable-chips
                    data-cy="raceDetailed"
                    clearable
                    @update:model-value="raceDetailedChangeHandler"
                >
                    <template #prepend-item>
                        <div class="pa-2 text-caption d-flex justify-space-between">
                            Press the ESC key to close this list
                            <button class="mr-2" @click="closeMenu('raceDetailedSelect')">
                                <font-awesome-icon size="lg" :icon="['far', 'times']" class="mr-2" />
                            </button>
                        </div>
                    </template>
                    <template #no-data>
                        <div class="pa-4 body-2">
                            {{ loadingRaceDetailedSearch ? 'Searching...' : 'Search for detailed race' }}
                        </div>
                    </template>
                </v-autocomplete>
                <v-autocomplete
                    v-model="humanData.ethnicityCategory"
                    label="Ethnicity"
                    :items="ethnicityCategoryOptions"
                    item-title="display"
                    searchable
                    return-object
                    small-chips
                    deletable-chips
                    data-cy="ethnicityCategory"
                    @update:update:model-value="ethnicityCategoryChangeHandler"
                >
                    <template #no-data>
                        <v-list-item>
                            <v-list-item-title> Ethnicity not found </v-list-item-title>
                        </v-list-item>
                    </template>
                </v-autocomplete>
                <v-autocomplete
                    v-if="!isWidget"
                    ref="ethinicityDetailedSelect"
                    v-model="humanData.ethnicityDetailed"
                    v-model:search-input="ethnicityDetailedSearch"
                    label="Ethnicity detailed"
                    :items="ethnicityDetailedOptions"
                    item-title="display"
                    searchable
                    return-object
                    multiple
                    small-chips
                    deletable-chips
                    clearable
                    data-cy="ethnicityDetailed"
                    @update:model-value="ethnicityDetailedChangeHandler"
                >
                    <template #prepend-item>
                        <div class="pa-2 text-caption d-flex justify-space-between">
                            Press the ESC key to close this list
                            <button class="mr-2" @click="closeMenu('ethinicityDetailedSelect')">
                                <font-awesome-icon size="lg" :icon="['far', 'times']" class="mr-2" />
                            </button>
                        </div>
                    </template>
                    <template #no-data>
                        <v-list-item>
                            <v-list-item-title> Ethnicity Details not found </v-list-item-title>
                        </v-list-item>
                    </template>
                </v-autocomplete>
            </template>

            <div class="bn-section-header">Additional demographics</div>
            <v-select
                v-if="humanData.resourceType === 'Patient'"
                v-model="humanData.maritalStatus"
                label="Marital status"
                :items="maritalStatusOptions"
                item-title="coding[0].display"
                return-object
                clearable
                data-cy="contactEditHumanMaritalStatus"
                @click:clear="humanData.maritalStatus = undefined"
            ></v-select>

            <v-select
                v-if="resource.resourceType === 'Practitioner'"
                v-model="humanData.spokenLanguage"
                label="Preferred language"
                :items="preferredLanguages"
                item-title="coding[0].display"
                return-object
                clearable
                data-cy="contactEditHumanSpokenLanguage"
            ></v-select>
            <v-select
                v-else
                v-model="humanData.spokenLanguage"
                label="Preferred language"
                :items="preferredLanguages"
                item-title="language.coding[0].display"
                return-object
                clearable
                data-cy="contactEditHumanPerferredLanguage"
                @click:clear="humanData.spokenLanguage = undefined"
            ></v-select>
            <v-text-field
                v-if="humanData.resourceType === 'Patient'"
                v-model="humanData.ssn"
                v-facade="'###-##-####'"
                label="Social security number"
                v-bind="$bnVuelidateErrorExtractor(v$.humanData, 'ssn', true)"
                :class="{ usprivacy: getUSPrivacy(resource) }"
                data-cy="contactEditHumanSSN"
                @blur="v$.humanData.ssn.$touch()"
            >
                <template #append>
                    <BnInfo type="dialog" color="warning" size="lg">
                        Only collect a Social Security Number (SSN) if absolutely necessary. Many payers/providers actively purge SSNs from their systems and/or filter them from incoming data.
                    </BnInfo>
                </template>
            </v-text-field>
            <v-text-field v-if="humanData.resourceType === 'Patient'" v-model="humanData.driversLicense" label="Driver license number" class="usprivacy"></v-text-field>
            <v-select
                v-if="!isResourceUser(humanData)"
                v-model="humanData.religion"
                label="Religion"
                :items="religiousAffiliationOptions"
                item-title="coding[0].display"
                return-object
                clearable
                data-cy="contactEditHumanReligion"
                @click:clear="humanData.religion = undefined"
            ></v-select>
            <v-text-field v-if="isPatient" v-model="humanData.employerSchool" label="Employer and occupation or school" data-cy="contactEditHumanEmployerSchool"></v-text-field>
            <div>
                <v-textarea v-if="!isWidget" ref="keyComments" v-model="humanData.keyComments" label="Helpful comments" filled auto-grow rows="1" data-cy="keyCommentsTextarea" class="usprivacy">
                    <template v-if="isPatient" #append>
                        <BnInfo type="dialog" size="lg">Use helpful comments to note any important, non-clinical, details to better serve this {{ authStore.clientVocab.toLowerCase() }}. </BnInfo>
                    </template>
                </v-textarea>
            </div>
            <BnDialog ref="bnDialog" />

            <v-dialog v-if="showContactPointDialog" v-model="showContactPointDialog" scrollable persistent :fullscreen="$vuetify.display.xs" max-width="450px">
                <BnContactPointEdit
                    id="bn-contact-point-edit"
                    :mode="workingContactPoint.temp.mode"
                    :disable-preferred-check="workingContactPoint.temp.disablePreferred"
                    :resource="humanData"
                    :contact-point="workingContactPoint"
                    @cancel="showContactPointDialog = false"
                    @save="showContactPointDialog = false"
                ></BnContactPointEdit>
            </v-dialog>

            <v-dialog v-if="showAddressDialog" v-model="showAddressDialog" scrollable persistent :fullscreen="$vuetify.display.xs" max-width="450px">
                <BnContactAddress
                    id="bn-contact-address"
                    :mode="workingAddress.temp.mode"
                    :disable-preferred-check="workingAddress.temp.disablePreferred"
                    :resource="humanData"
                    :address="workingAddress"
                    @cancel="showAddressDialog = false"
                    @save="showAddressDialog = false"
                ></BnContactAddress>
            </v-dialog>

            <DataProvider ref="careTeamDataProvider" v-model="careTeamData" :query="careTeamQuery" @data-changed="onCareTeamDataChanged" />
        </template>
    </DataProvider>
</template>

<style>
.program-select {
    width: 100%;
}

.program-list-item {
    min-width: 0;
    padding-right: 16px;
}

/* Ensure the input text doesn't overflow */
.program-select .v-field__input {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
}

/* Add padding for the warning icon when present */
.program-select .v-field--prepended {
    padding-left: 12px;
}

.v-card.v-theme--lightTheme.v-card--density-compact.v-card--variant-flat.no-select .d-flex .bn-section-header {
    margin-top: 8px;
}
.v-card.v-theme--lightTheme.v-card--density-compact.v-card--variant-flat.no-select .d-flex .bn-section-header .ml-4.d-flex {
    margin-bottom: -4px;
}
</style>

<style scoped>
.v-list-item {
    min-height: 64px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
}

.text-truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.primary-provider-checkbox :deep(.v-label) {
    width: 80px;
    white-space: normal;
}
</style>
